/**
 * Internationalization (i18n) Module
 *
 * Lightweight translation system for BisaWofa.
 * Supports Ghana/West Africa languages: English, Twi, French, Hausa, Ewe, Ga.
 *
 * Usage:
 *   import { t, setLanguage, getLanguage, onLanguageChange } from './i18n.js';
 *
 *   t('nav.savedLocations')  // "Saved Locations" (en)
 *   setLanguage('tw')        // Switch to Twi
 *   t('nav.savedLocations')  // "Nsaemfua a woadwuma" (tw)
 */

const STORAGE_KEY = 'bisawofa-lang';
const DEFAULT_LANG = 'en';

// Current language
let currentLang = localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;

// Language change listeners
const listeners = new Set();

// Available languages with metadata
export const languages = {
  en: { name: 'English', nativeName: 'English', flag: '🇬🇧' },
  tw: { name: 'Twi', nativeName: 'Twi (Akan)', flag: '🇬🇭' },
  fr: { name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  ha: { name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬' },
  ee: { name: 'Ewe', nativeName: 'Eʋegbe', flag: '🇬🇭' },
  ga: { name: 'Ga', nativeName: 'Gã', flag: '🇬🇭' },
};

// Translation strings keyed by language code
const translations = {
  // ──────────────────────────────────────────────────────────────────────────
  // English
  // ──────────────────────────────────────────────────────────────────────────
  en: {
    // Navigation & Layout
    'nav.savedLocations': 'Saved Locations',
    'nav.leftPanel': 'Left Panel',
    'nav.bottomPanel': 'Bottom Panel',
    'nav.openLeft': 'Open left panel',
    'nav.openRight': 'Open right panel',
    'nav.openBottom': 'Open bottom panel',

    // Dock buttons
    'dock.layers': 'Layers',
    'dock.locate': 'Locate',
    'dock.add': 'Add',
    'dock.circle': 'Circle',
    'dock.distance': 'Distance',
    'dock.clear': 'Clear',
    'dock.settings': 'Settings',

    // Map
    'map.searchPlaceholder': 'Search location...',
    'map.myLocation': 'My Location',

    // Locations panel
    'locations.title': 'Locations',
    'locations.tip': 'Tap on the map to add a new location.',
    'locations.empty': 'No locations yet.',
    'locations.emptyHint': 'Tap the map to add one!',
    'locations.deleted': 'Location deleted.',
    'locations.deleteError': 'Could not delete location.',
    'locations.fitAll': 'Fit All',
    'locations.export': 'Export',
    'locations.exportGeoJSON': 'GeoJSON',
    'locations.status': 'Status',
    'locations.addedSuccess': 'Location added successfully',
    'locations.addFailed': 'Failed to add location',

    // Add Location popup
    'addLocation.title': 'Add Location',
    'addLocation.name': 'Name',
    'addLocation.namePlaceholder': 'e.g., Water Point A',
    'addLocation.category': 'Category',
    'addLocation.description': 'Description',
    'addLocation.descriptionPlaceholder': 'Optional notes...',
    'addLocation.submit': 'Add Location',

    // Categories
    'category.default': 'Default',
    'category.water': 'Water Point',
    'category.school': 'School',
    'category.health': 'Health Facility',
    'category.hotel': 'Hotel',
    'category.restaurant': 'Restaurant',
    'category.market': 'Market',
    'category.other': 'Other',

    // Status modal
    'status.title': 'Database Status',
    'status.ready': 'Ready',
    'status.online': 'Online',
    'status.offline': 'Offline',
    'status.database': 'Database',
    'status.tables': 'Tables',
    'status.locations': 'Locations',
    'status.yes': 'Yes',
    'status.no': 'No',
    'status.close': 'Close',
    'status.loading': 'Loading...',

    // PWA
    'pwa.install': 'Install App',
    'pwa.offline': 'OFFLINE',
    'pwa.updateAvailable': 'A new version is available. Reload now?',

    // General
    'general.error': 'Error',
    'general.success': 'Success',
    'general.cancel': 'Cancel',
    'general.save': 'Save',
    'general.delete': 'Delete',
    'general.loading': 'Loading...',
    'general.language': 'Language',

    // Settings
    'settings.fieldwork': 'Fieldwork Mode',
    'settings.fieldworkDesc': 'High contrast & large targets for outdoor use',
    'settings.leftHanded': 'Left-handed',
    'settings.leftHandedDesc': 'Move the control wheel to the left edge for easier thumb reach',
    'settings.credits': 'Credits',
    'settings.startScreen': 'Start screen',
    'settings.startScreenDesc': 'Show the choices screen when the app opens, instead of going straight to the map',
    'settings.creditsDesc': 'The open-source projects BisaWofa is built on',
    'settings.creditsView': 'View',
    'settings.fontSize': 'Font Size',
    'settings.fontSizeDesc': 'Adjust text size for readability',
    'settings.presence': 'Track My Location',
    'settings.presenceDesc': 'Regularly records where you are so BisaWofa can warn you of nearby emergencies and suggest places around you',
    'settings.userType': 'I am a…',
    'settings.userTypeDesc': 'Your role adds extra options when you add a place (e.g. menu specials)',
    'settings.account': 'Account',
    'settings.signOut': 'Sign out',
    'editLocation.title': 'Edit place',
    'editLocation.submit': 'Save changes',
    'editLocation.saved': 'Changes saved',

    // Voice input
    'voice.listening': 'Listening…',
    'voice.noResult': 'No speech detected. Try again.',
    'voice.error': 'Voice input failed',

    // Errors
    'error.dbInit': 'Failed to initialize database. Please refresh the page.',
    'error.export': 'Export failed',
    'error.requiredFields': 'Please fill in all required fields',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Twi (Akan)
  // ──────────────────────────────────────────────────────────────────────────
  tw: {
    'nav.savedLocations': 'Nneɛma a wɔakora',
    'nav.leftPanel': 'Benkum fa',
    'nav.bottomPanel': 'Ase fa',
    'nav.openLeft': 'Bue benkum panel',
    'nav.openRight': 'Bue nifa panel',
    'nav.openBottom': 'Bue ase panel',

    'dock.layers': 'Nhyehyɛe',
    'dock.locate': 'Hwehwɛ',
    'dock.add': 'Fa ka ho',
    'dock.circle': 'Sɛklɛ',
    'dock.distance': 'Kwan',
    'dock.clear': 'Pepa',
    'dock.settings': 'Nhyehyɛe',

    'map.searchPlaceholder': 'Hwehwɛ beae...',
    'map.myLocation': 'Me beae',

    'locations.title': 'Nneɛma',
    'locations.tip': 'Tẽ map no so de ka beae foforo ho.',
    'locations.empty': 'Beae biara nni hɔ.',
    'locations.emptyHint': 'Tẽ map no so de ka bi ho!',
    'locations.deleted': 'Beae no apopa.',
    'locations.deleteError': 'Antumi ampopa beae no.',
    'locations.fitAll': 'Hwɛ nyinaa',
    'locations.export': 'Yi kɔ',
    'locations.exportGeoJSON': 'GeoJSON',
    'locations.status': 'Tebea',
    'locations.addedSuccess': 'Wɔde beae no aka ho',
    'locations.addFailed': 'Anka beae no ka ho no anyɛ yie',

    'addLocation.title': 'Fa beae ka ho',
    'addLocation.name': 'Din',
    'addLocation.namePlaceholder': 'Sɛ, Nsuo ase A',
    'addLocation.category': 'Nkyekyɛmu',
    'addLocation.description': 'Nsɛm',
    'addLocation.descriptionPlaceholder': 'Nsɛm foforo...',
    'addLocation.submit': 'Fa ka ho',

    'category.default': 'Dɛfo',
    'category.water': 'Nsuo ase',
    'category.school': 'Sukuu',
    'category.health': 'Ayaresabea',
    'category.hotel': 'Ahɔhobea',
    'category.restaurant': 'Aduanebea',
    'category.market': 'Dwam',
    'category.other': 'Afoforo',

    'status.title': 'Database tebea',
    'status.ready': 'Asiesie',
    'status.online': 'Wɔ intanɛt so',
    'status.offline': 'Intanɛt nni hɔ',
    'status.database': 'Database',
    'status.tables': 'Pon',
    'status.locations': 'Nneɛma',
    'status.yes': 'Aane',
    'status.no': 'Daabi',
    'status.close': 'To mu',
    'status.loading': 'Ɛreloade...',

    'pwa.install': 'Instɔl App',
    'pwa.offline': 'INTANƐT NNI HƆ',
    'pwa.updateAvailable': 'Nkabom foforo wɔ hɔ. Reload afei?',

    'general.error': 'Mfomso',
    'general.success': 'Ɛyɛ',
    'general.cancel': 'Gyae',
    'general.save': 'Kora',
    'general.delete': 'Popa',
    'general.loading': 'Ɛreloade...',
    'general.language': 'Kasa',

    'settings.fieldwork': 'Adwumayɛ kwan',
    'settings.fieldworkDesc': 'Nkontrahyɛ kɛse ne touch kɛse ma abɔnten',
    'settings.leftHanded': 'Benkum nsa',
    'settings.leftHandedDesc': 'Fa control wheel no kɔ benkum fam sɛnea wo kokurobeti bɛtumi aka',
    'settings.credits': 'Aseda',
    'settings.startScreen': 'Mfiase krataa',
    'settings.startScreenDesc': 'Kyerɛ nneɛma a wobɛpaw bere a wobue app no, sen sɛ wobɛkɔ asase mfoni no so tee',
    'settings.creditsDesc': 'Nnwuma a wɔde bɔɔ BisaWofa a obiara tumi de di dwuma',
    'settings.creditsView': 'Hwɛ',
    'settings.fontSize': 'Lɛta kɛse',
    'settings.fontSizeDesc': 'Sesa lɛta kɛse na akenkan',
    'settings.presence': 'Hwɛ Faako A Mewɔ',
    'settings.presenceDesc': 'Ɛkyerɛw faako a wowɔ bere biara sɛnea BisaWofa betumi abɔ wo kɔkɔ wɔ amanehunu a ɛbɛn ho na akyerɛ mmeae a ɛbɛn wo',
    'settings.userType': 'Meyɛ…',
    'settings.userTypeDesc': 'Wo dwuma no de akwan foforo ka ho bere a wode beae bi ka ho (sɛ aduane titiriw)',
    'settings.account': 'Akontaabu',
    'settings.signOut': 'Pue',
    'editLocation.title': 'Sesa beae',
    'editLocation.submit': 'Kora nsakraeɛ',
    'editLocation.saved': 'Wɔakora nsakraeɛ',

    'voice.listening': 'Ɛretie…',
    'voice.noResult': 'Wɔante kasa biara. Bɔ mmɔden bio.',
    'voice.error': 'Nne nkanfo no anyɛ yie',

    'error.dbInit': 'Database no antumi anhyɛ ase. Yɛsrɛ wo san hye ase.',
    'error.export': 'Yi kɔ no anyɛ yie',
    'error.requiredFields': 'Yɛsrɛ wo hyɛ mu nyinaa ma',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // French
  // ──────────────────────────────────────────────────────────────────────────
  fr: {
    'nav.savedLocations': 'Lieux enregistrés',
    'nav.leftPanel': 'Panneau gauche',
    'nav.bottomPanel': 'Panneau inférieur',
    'nav.openLeft': 'Ouvrir le panneau gauche',
    'nav.openRight': 'Ouvrir le panneau droit',
    'nav.openBottom': 'Ouvrir le panneau inférieur',

    'dock.layers': 'Couches',
    'dock.locate': 'Localiser',
    'dock.add': 'Ajouter',
    'dock.circle': 'Cercle',
    'dock.distance': 'Distance',
    'dock.clear': 'Effacer',
    'dock.settings': 'Paramètres',

    'map.searchPlaceholder': 'Rechercher un lieu...',
    'map.myLocation': 'Ma position',

    'locations.title': 'Lieux',
    'locations.tip': 'Appuyez sur la carte pour ajouter un nouveau lieu.',
    'locations.empty': 'Aucun lieu pour le moment.',
    'locations.emptyHint': 'Appuyez sur la carte pour en ajouter!',
    'locations.deleted': 'Lieu supprimé.',
    'locations.deleteError': 'Impossible de supprimer le lieu.',
    'locations.fitAll': 'Tout voir',
    'locations.export': 'Exporter',
    'locations.exportGeoJSON': 'GeoJSON',
    'locations.status': 'Statut',
    'locations.addedSuccess': 'Lieu ajouté avec succès',
    'locations.addFailed': "Échec de l'ajout du lieu",

    'addLocation.title': 'Ajouter un lieu',
    'addLocation.name': 'Nom',
    'addLocation.namePlaceholder': 'ex., Point d\'eau A',
    'addLocation.category': 'Catégorie',
    'addLocation.description': 'Description',
    'addLocation.descriptionPlaceholder': 'Notes optionnelles...',
    'addLocation.submit': 'Ajouter le lieu',

    'category.default': 'Par défaut',
    'category.water': 'Point d\'eau',
    'category.school': 'École',
    'category.health': 'Centre de santé',
    'category.hotel': 'Hôtel',
    'category.restaurant': 'Restaurant',
    'category.market': 'Marché',
    'category.other': 'Autre',

    'status.title': 'Statut de la base de données',
    'status.ready': 'Prêt',
    'status.online': 'En ligne',
    'status.offline': 'Hors ligne',
    'status.database': 'Base de données',
    'status.tables': 'Tables',
    'status.locations': 'Lieux',
    'status.yes': 'Oui',
    'status.no': 'Non',
    'status.close': 'Fermer',
    'status.loading': 'Chargement...',

    'pwa.install': 'Installer',
    'pwa.offline': 'HORS LIGNE',
    'pwa.updateAvailable': 'Une nouvelle version est disponible. Recharger maintenant?',

    'general.error': 'Erreur',
    'general.success': 'Succès',
    'general.cancel': 'Annuler',
    'general.save': 'Enregistrer',
    'general.delete': 'Supprimer',
    'general.loading': 'Chargement...',
    'general.language': 'Langue',

    'settings.fieldwork': 'Mode terrain',
    'settings.fieldworkDesc': 'Contraste fort et cibles larges pour le terrain',
    'settings.leftHanded': 'Gaucher',
    'settings.leftHandedDesc': 'Déplace la roue de commande vers le bord gauche pour le pouce',
    'settings.credits': 'Remerciements',
    'settings.startScreen': 'Écran d’accueil',
    'settings.startScreenDesc': 'Afficher l’écran de choix à l’ouverture, au lieu d’aller directement à la carte',
    'settings.creditsDesc': 'Les projets open source sur lesquels BisaWofa est construit',
    'settings.creditsView': 'Voir',
    'settings.fontSize': 'Taille du texte',
    'settings.fontSizeDesc': 'Ajuster la taille du texte',
    'settings.presence': 'Suivre ma position',
    'settings.presenceDesc': 'Enregistre régulièrement votre position pour que BisaWofa puisse vous alerter des urgences à proximité et vous suggérer des lieux',
    'settings.userType': 'Je suis…',
    'settings.userTypeDesc': 'Votre rôle ajoute des options quand vous ajoutez un lieu (ex. plats du jour)',
    'settings.account': 'Compte',
    'settings.signOut': 'Se déconnecter',
    'editLocation.title': 'Modifier le lieu',
    'editLocation.submit': 'Enregistrer',
    'editLocation.saved': 'Modifications enregistrées',

    'voice.listening': 'Écoute…',
    'voice.noResult': 'Aucune voix détectée. Réessayez.',
    'voice.error': 'Saisie vocale échouée',

    'error.dbInit': 'Échec de l\'initialisation de la base de données. Veuillez rafraîchir la page.',
    'error.export': 'Échec de l\'exportation',
    'error.requiredFields': 'Veuillez remplir tous les champs obligatoires',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Hausa
  // ──────────────────────────────────────────────────────────────────────────
  ha: {
    'nav.savedLocations': 'Wuraren da aka ajiye',
    'nav.leftPanel': 'Bangaren hagu',
    'nav.bottomPanel': 'Bangaren ƙasa',
    'nav.openLeft': 'Buɗe bangaren hagu',
    'nav.openRight': 'Buɗe bangaren dama',
    'nav.openBottom': 'Buɗe bangaren ƙasa',

    'dock.layers': 'Yadudduka',
    'dock.locate': 'Nemo',
    'dock.add': 'Ƙara',
    'dock.circle': "Da'ira",
    'dock.distance': 'Nisa',
    'dock.clear': 'Share',
    'dock.settings': 'Saituna',

    'map.searchPlaceholder': 'Nemo wuri...',
    'map.myLocation': 'Wurina',

    'locations.title': 'Wurare',
    'locations.tip': 'Danna taswirar don ƙara sabon wuri.',
    'locations.empty': 'Babu wurare tukuna.',
    'locations.emptyHint': 'Danna taswirar don ƙara!',
    'locations.deleted': 'An share wurin.',
    'locations.deleteError': 'Ba a iya share wurin ba.',
    'locations.fitAll': 'Duba duka',
    'locations.export': 'Fitar',
    'locations.exportGeoJSON': 'GeoJSON',
    'locations.status': 'Matsayi',
    'locations.addedSuccess': 'An ƙara wurin cikin nasara',
    'locations.addFailed': 'Ƙara wurin bai yi nasara ba',

    'addLocation.title': 'Ƙara wuri',
    'addLocation.name': 'Suna',
    'addLocation.namePlaceholder': 'misali, Wurin ruwa A',
    'addLocation.category': 'Rukuni',
    'addLocation.description': 'Bayani',
    'addLocation.descriptionPlaceholder': 'Ƙarin bayani...',
    'addLocation.submit': 'Ƙara wuri',

    'category.default': 'Na asali',
    'category.water': 'Wurin ruwa',
    'category.school': 'Makaranta',
    'category.health': 'Asibitin',
    'category.hotel': 'Otel',
    'category.restaurant': 'Gidan abinci',
    'category.market': 'Kasuwa',
    'category.other': 'Wani',

    'status.title': 'Matsayin bayanai',
    'status.ready': 'A shirye',
    'status.online': 'A kan layi',
    'status.offline': 'Ba a kan layi ba',
    'status.database': 'Bayanai',
    'status.tables': 'Tebura',
    'status.locations': 'Wurare',
    'status.yes': 'Ee',
    'status.no': "A'a",
    'status.close': 'Rufe',
    'status.loading': 'Ana lodawa...',

    'pwa.install': 'Shigar da manhaja',
    'pwa.offline': 'BA A KAN LAYI BA',
    'pwa.updateAvailable': 'Sabon sigar yana nan. Sake lodawa yanzu?',

    'general.error': 'Kuskure',
    'general.success': 'Nasara',
    'general.cancel': 'Soke',
    'general.save': 'Ajiye',
    'general.delete': 'Share',
    'general.loading': 'Ana lodawa...',
    'general.language': 'Harshe',

    'settings.fieldwork': 'Yanayin filin',
    'settings.fieldworkDesc': 'Bambancin launi mai ƙarfi da manyan maɓallai don waje',
    'settings.leftHanded': 'Mai hannun hagu',
    'settings.leftHandedDesc': 'Matsar da dabaran sarrafawa zuwa gefen hagu don sauƙin babban yatsa',
    'settings.credits': 'Godiya',
    'settings.startScreen': 'Allon farawa',
    'settings.startScreenDesc': 'Nuna allon zaɓi lokacin buɗe manhajar, maimakon zuwa taswira kai tsaye',
    'settings.creditsDesc': 'Ayyukan buɗaɗɗen tushe da aka gina BisaWofa a kansu',
    'settings.creditsView': 'Duba',
    'settings.fontSize': 'Girman rubutu',
    'settings.fontSizeDesc': 'Daidaita girman rubutu don karatu',
    'settings.presence': 'Bin Diddigin Wurina',
    'settings.presenceDesc': 'Yana yin rikodin inda kake akai-akai domin BisaWofa ta iya gargaɗe ka game da gaggawa kusa da kai da ba da shawarar wurare kusa da kai',
    'settings.userType': 'Ni…',
    'settings.userTypeDesc': 'Matsayinka yana ƙara ƙarin zaɓuɓɓuka lokacin da ka ƙara wuri (misali abincin rana na musamman)',
    'settings.account': 'Asusu',
    'settings.signOut': 'Fita',
    'editLocation.title': 'Gyara wuri',
    'editLocation.submit': 'Ajiye canje-canje',
    'editLocation.saved': 'An ajiye canje-canje',

    'voice.listening': 'Ana sauraro…',
    'voice.noResult': 'Ba a gane magana ba. Sake gwadawa.',
    'voice.error': 'Shigar murya ta kasa',

    'error.dbInit': 'Fara bayanai bai yi nasara ba. Da fatan za a sake loda shafin.',
    'error.export': 'Fitar bai yi nasara ba',
    'error.requiredFields': 'Da fatan za a cika dukkan filayen da ake buƙata',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Ewe
  // ──────────────────────────────────────────────────────────────────────────
  ee: {
    'nav.savedLocations': 'Teƒewo si wodzra',
    'nav.leftPanel': 'Miã panel',
    'nav.bottomPanel': 'Ete panel',
    'nav.openLeft': 'Ʋu miã panel',
    'nav.openRight': 'Ʋu ɖusime panel',
    'nav.openBottom': 'Ʋu ete panel',

    'dock.layers': 'Akpawo',
    'dock.locate': 'Dze edzi',
    'dock.add': 'Tsɔ kpe ɖe eŋu',
    'dock.circle': 'Logo',
    'dock.distance': 'Ga',
    'dock.clear': 'Tutui',
    'dock.settings': 'Ðoɖowo',

    'map.searchPlaceholder': 'Di teƒe...',
    'map.myLocation': 'Nye teƒe',

    'locations.title': 'Teƒewo',
    'locations.tip': 'Zi map dzi atsɔ teƒe yeye akpe ɖe eŋu.',
    'locations.empty': 'Teƒe aɖeke meli oa.',
    'locations.emptyHint': 'Zi map dzi natsɔ ɖeka akpe ɖe eŋu!',
    'locations.deleted': 'Wotutui teƒea.',
    'locations.deleteError': 'Mate ŋu atutui teƒea o.',
    'locations.fitAll': 'Kpɔ katã',
    'locations.export': 'Ɖoe ɖa',
    'locations.exportGeoJSON': 'GeoJSON',
    'locations.status': 'Nɔnɔme',
    'locations.addedSuccess': 'Wɔtsɔ teƒe la kpe ɖe eŋu',
    'locations.addFailed': 'Teƒe la tsɔ kpe ɖe eŋu mesɔ o',

    'addLocation.title': 'Tsɔ teƒe kpe ɖe eŋu',
    'addLocation.name': 'Ŋkɔ',
    'addLocation.namePlaceholder': 'Kpɔɖeŋu, Tsiƒe A',
    'addLocation.category': 'Ƒuƒoƒo',
    'addLocation.description': 'Nuŋlɔɖi',
    'addLocation.descriptionPlaceholder': 'Nuŋlɔɖi ɖewo...',
    'addLocation.submit': 'Tsɔ kpe ɖe eŋu',

    'category.default': 'Gɔme',
    'category.water': 'Tsiƒe',
    'category.school': 'Sukuu',
    'category.health': 'Dɔyɔƒe',
    'category.hotel': 'Hɔtɛl',
    'category.restaurant': 'Nuɖuƒe',
    'category.market': 'Asi',
    'category.other': 'Bubu',

    'status.title': 'Database nɔnɔme',
    'status.ready': 'Sɔ ɖo',
    'status.online': 'Le intanɛt dzi',
    'status.offline': 'Mele intanɛt dzi o',
    'status.database': 'Database',
    'status.tables': 'Kplɔ̃wo',
    'status.locations': 'Teƒewo',
    'status.yes': 'Ɛ̃',
    'status.no': 'Ao',
    'status.close': 'Xɔ tu',
    'status.loading': 'Ele dzadzam...',

    'pwa.install': 'Da app la ɖo',
    'pwa.offline': 'INTANƐT MELI O',
    'pwa.updateAvailable': 'Tɔtrɔ yeye li. Nàgbugbɔ alo fifia?',

    'general.error': 'Vodada',
    'general.success': 'Edze',
    'general.cancel': 'Ɖoe ɖa',
    'general.save': 'Dzra ɖi',
    'general.delete': 'Tutui',
    'general.loading': 'Ele dzadzam...',
    'general.language': 'Gbe',

    'settings.fieldwork': 'Dɔwɔƒe nɔnɔme',
    'settings.fieldworkDesc': 'Nuŋɔŋlɔ gã kple zi gã na adzame dɔwɔwɔ',
    'settings.leftHanded': 'Miasi zɔla',
    'settings.leftHandedDesc': 'Ʋã kpɔɖeŋu ƒe tsyɔ̃e la ɖe miame be asibidɛ gãtɔ nate ŋu aɖo',
    'settings.credits': 'Akpedada',
    'settings.startScreen': 'Gɔmedzedze ƒe axa',
    'settings.startScreenDesc': 'Ɖe tiatiawo ƒe axa fia ne èʋu app la, le esi nàyi anyigbatata la gbɔ tẽ teƒe',
    'settings.creditsDesc': 'Dɔ siwo dzi wotu BisaWofa ɖo si le ʋuʋu na amesiame',
    'settings.creditsView': 'Kpɔ',
    'settings.fontSize': 'Nuŋɔŋlɔ kɛse',
    'settings.fontSizeDesc': 'Trɔ nuŋɔŋlɔ kɛse na xlexle',
    'settings.presence': 'Kpɔ Nye Nɔƒe',
    'settings.presenceDesc': 'Eŋlɔa afi si nèle ɣesiaɣi ale be BisaWofa nate ŋu axlɔ̃ nu wò le afɔku siwo te ɖe ŋuwò ŋu eye wòaɖo teƒe siwo te ɖe ŋuwò',
    'settings.userType': 'Menye…',
    'settings.userTypeDesc': 'Wò dɔwɔƒe tsɔa mɔnukpɔkpɔ bubuwo kpena ne nètsɔ teƒe aɖe kpe ɖe eŋu (le kpɔɖeŋu nuɖuɖu tɔxɛwo)',
    'settings.account': 'Akɔnta',
    'settings.signOut': 'Do go',
    'editLocation.title': 'Trɔ asi le teƒea ŋu',
    'editLocation.submit': 'Dzra tɔtrɔwo ɖo',
    'editLocation.saved': 'Wodzra tɔtrɔwo ɖo',

    'voice.listening': 'Ele tɔsese…',
    'voice.noResult': 'Mese gbe aɖeke o. Gbugbɔ te kpɔ.',
    'voice.error': 'Gbe ɖoɖo me dze o',

    'error.dbInit': 'Database me dze o. Mede kuku gbugbɔ page la.',
    'error.export': 'Mete ŋu do kɔ o',
    'error.requiredFields': 'Mede kuku wɔ gɔmewo katã',
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Ga
  // ──────────────────────────────────────────────────────────────────────────
  ga: {
    'nav.savedLocations': 'Teŋshishi ni wɔhã',
    'nav.leftPanel': 'Gbɛi lɛ panel',
    'nav.bottomPanel': 'Etee panel',
    'nav.openLeft': 'Hulu gbɛi panel',
    'nav.openRight': 'Hulu ŋwɛi panel',
    'nav.openBottom': 'Hulu etee panel',

    'dock.layers': 'Akɛshishi',
    'dock.locate': 'Hwala',
    'dock.add': 'Ka he',
    'dock.circle': 'Bubi',
    'dock.distance': 'Djidji',
    'dock.clear': 'Gble',
    'dock.settings': 'Sɛtiiŋ',

    'map.searchPlaceholder': 'Hwala teŋ...',
    'map.myLocation': 'Mi teŋ',

    'locations.title': 'Teŋshishi',
    'locations.tip': 'Tee map lɛ ji bo ka teŋ fofoi he.',
    'locations.empty': 'Teŋ kome nɛɛ shi.',
    'locations.emptyHint': 'Tee map lɛ ji bo ka okome he!',
    'locations.deleted': 'Wokpo teŋ lɛ.',
    'locations.deleteError': 'Mibaa kpo teŋ lɛ fɛɛ.',
    'locations.fitAll': 'Kɛ amɛnɛ',
    'locations.export': 'Yi ko',
    'locations.exportGeoJSON': 'GeoJSON',
    'locations.status': 'Lɛ bɔ nɔ',
    'locations.addedSuccess': 'Wɔka teŋ lɛ he yɛ',
    'locations.addFailed': 'Teŋ lɛ ka he lɛ mɛyɛ o',

    'addLocation.title': 'Ka teŋ he',
    'addLocation.name': 'Ŋmɛlɛ',
    'addLocation.namePlaceholder': 'sɛ, Nu tsoo A',
    'addLocation.category': 'Baabia',
    'addLocation.description': 'Yɛ lɛ shishi',
    'addLocation.descriptionPlaceholder': 'Shishi fofoi...',
    'addLocation.submit': 'Ka he',

    'category.default': 'Gɔmɛ',
    'category.water': 'Nu tsoo',
    'category.school': 'Sukuu',
    'category.health': 'Asɔfalɛ',
    'category.hotel': 'Hotɛl',
    'category.restaurant': 'Niikɛlɛ bii',
    'category.market': 'Omaŋ',
    'category.other': 'Fofoi',

    'status.title': 'Database lɛ bɔ nɔ',
    'status.ready': 'Efee lɛ',
    'status.online': 'Wɔ shi intanɛt ji',
    'status.offline': 'Intanɛt nɛɛ shi',
    'status.database': 'Database',
    'status.tables': 'Teebul',
    'status.locations': 'Teŋshishi',
    'status.yes': 'Ɛhɛɛ',
    'status.no': 'Daabi',
    'status.close': 'To',
    'status.loading': 'Yɛ loode...',

    'pwa.install': 'Yɛ instɔl',
    'pwa.offline': 'INTANƐT NƐƐ SHI',
    'pwa.updateAvailable': 'Fofoi li. Yɛ loode fɛɛ?',

    'general.error': 'Fomɔ',
    'general.success': 'Eyɛ',
    'general.cancel': 'Yɛ tɔɔ',
    'general.save': 'Hã',
    'general.delete': 'Yɛ shwane',
    'general.loading': 'Yɛ loode...',
    'general.language': 'Wiemɔ',

    'settings.fieldwork': 'Abɔnten dɔ',
    'settings.fieldworkDesc': 'Nkontrahyɛ kɛse kɛ maɓalli kɛse ni abɔnten dɔ',
    'settings.leftHanded': 'Abɛku nine',
    'settings.leftHandedDesc': 'Kɛ control wheel lɛ ya abɛku he koni onaakwɛi anya',
    'settings.credits': 'Shidaa',
    'settings.startScreen': 'Shishijee krokoto',
    'settings.startScreenDesc': 'Tsɔɔ nihiamɔi krokoto lɛ be ogbeleɔ app lɛ, ni jeee oyaa mapu lɛ nɔ tee',
    'settings.creditsDesc': 'Nitsumɔi ni akɛfee BisaWofa ni mɔ fɛɛ mɔ nyɛɔ ekɛtsuɔ nii',
    'settings.creditsView': 'Kwɛ',
    'settings.fontSize': 'Ŋmɛlɛ kɛse',
    'settings.fontSizeDesc': 'Trɔ ŋmɛlɛ kɛse ni kenkan',
    'settings.presence': 'Kwɛ He Ni Miyɔɔ',
    'settings.presenceDesc': 'Eŋmalaa he ni oyɔɔ be fɛɛ be koni BisaWofa anyɛ abɔ bo kɔkɔ yɛ oshara ni bɛŋ ohe he ni etsɔɔ hei ni bɛŋ bo',
    'settings.userType': 'Miji…',
    'settings.userTypeDesc': 'Onitsumɔ kɛ nibii krokomɛi baa he be mli ni oke he ko kɛfata he (akɛ niyenii krɛdɛɛ)',
    'settings.account': 'Akɔntaa',
    'settings.signOut': 'Je kpo',
    'editLocation.title': 'Saa he lɛ',
    'editLocation.submit': 'Hemɔ tsakemɔi',
    'editLocation.saved': 'Ahemɔ tsakemɔi',

    'voice.listening': 'Yɛ nuu…',
    'voice.noResult': 'Mɛnuu wiemɔ kome o. Yɛ te kpɔ.',
    'voice.error': 'Wiemɔ tsɔɔ lɛ mɛyɛ o',

    'error.dbInit': 'Database hyɛɛ ase lɛ mɛyɛ o. Ka refresh page lɛ.',
    'error.export': 'Yi ko lɛ mɛyɛ o',
    'error.requiredFields': 'Ka fill gɔmɛ amɛnɛ',
  },
};

/**
 * Get a translated string by key.
 * Falls back to English if key not found in current language.
 * Falls back to the key itself if not found in English either.
 */
export function t(key) {
  const langStrings = translations[currentLang];
  if (langStrings && langStrings[key] !== undefined) {
    return langStrings[key];
  }
  // Fallback to English
  if (translations.en[key] !== undefined) {
    return translations.en[key];
  }
  // Fallback to key
  console.warn(`[i18n] Missing translation: ${key} (${currentLang})`);
  return key;
}

/**
 * Set the active language.
 */
export function setLanguage(lang) {
  if (!translations[lang]) {
    console.warn(`[i18n] Unknown language: ${lang}`);
    return;
  }

  currentLang = lang;
  localStorage.setItem(STORAGE_KEY, lang);
  document.documentElement.lang = lang === 'tw' ? 'ak' : lang;

  // Notify listeners
  for (const listener of listeners) {
    try {
      listener(lang);
    } catch (e) {
      console.error('[i18n] Language change listener error:', e);
    }
  }

  // Update all DOM elements with data-i18n attributes
  updateDOM();

  console.log(`[i18n] Language set to: ${lang} (${languages[lang].nativeName})`);
}

/**
 * Get the current language code.
 */
export function getLanguage() {
  return currentLang;
}

/**
 * Get translated category label for a given category key.
 */
export function getCategoryLabel(category) {
  return t(`category.${category}`) || category;
}

/**
 * Map the current BisaWofa language to a BCP-47 speech recognition tag.
 * Falls back to 'en-GH' for languages with limited Speech API support.
 */
const speechLangMap = {
  en: 'en-GH',
  tw: 'ak-GH',   // Akan — Chrome may fall back to English
  fr: 'fr-FR',
  ha: 'ha-NG',
  ee: 'ee-GH',   // Ewe — limited support, may fall back
  ga: 'en-GH',   // Ga — no Speech API support yet, use English
};

export function getSpeechLang() {
  return speechLangMap[currentLang] || 'en-GH';
}

/**
 * Subscribe to language changes.
 * @returns {Function} Unsubscribe function.
 */
export function onLanguageChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Update all DOM elements that have data-i18n attributes.
 * Supports:
 *   data-i18n="key"            → sets textContent
 *   data-i18n-placeholder="key" → sets placeholder
 *   data-i18n-title="key"      → sets title attribute
 *   data-i18n-aria="key"       → sets aria-label
 */
export function updateDOM() {
  // Text content
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    if (key) el.textContent = t(key);
  });

  // Placeholder
  document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (key) el.placeholder = t(key);
  });

  // Title attribute
  document.querySelectorAll('[data-i18n-title]').forEach(el => {
    const key = el.getAttribute('data-i18n-title');
    if (key) el.title = t(key);
  });

  // Aria-label
  document.querySelectorAll('[data-i18n-aria]').forEach(el => {
    const key = el.getAttribute('data-i18n-aria');
    if (key) el.setAttribute('aria-label', t(key));
  });
}

/**
 * Initialize the i18n system. Call once on app start.
 */
export function initI18n() {
  // Set the document language
  document.documentElement.lang = currentLang === 'tw' ? 'ak' : currentLang;

  // Initial DOM update
  updateDOM();

  console.log(`[i18n] Initialized with language: ${currentLang} (${languages[currentLang]?.nativeName})`);
}

export default {
  t,
  setLanguage,
  getLanguage,
  getCategoryLabel,
  getSpeechLang,
  onLanguageChange,
  updateDOM,
  initI18n,
  languages,
};
